import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MdOutlineCancel } from 'react-icons/md';
import { useStateContext } from '../../../contexts/ContextProvider';
import { Button } from '../../../components';
import FormInput from '../../../components/FormField';
import { updateDocument, config, uploadData } from '../../../lib/appwrite';
import DropdownField from '../../../components/DropdownField';
import MessageInput from '../../../components/MessageInput';

const EditTestimonial = ({ view, setView, selectedRowData, setSelectedRowData, mode, onSuccess }) => {
  const { currentColor } = useStateContext();
  const isMountedRef = useRef(true);

  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastInitial, setLastInitial] = useState('');
  const [lastInitialError, setLastInitialError] = useState(false);
  const [review, setReview] = useState('');
  const [reviewError, setReviewError] = useState(false);
  const [status, setStatus] = useState('Waiting for Review');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    
    if (view && selectedRowData && mode === 'edit') {
      setFirstName(selectedRowData.firstName || '');
      setLastInitial(selectedRowData.lastInitial || '');
      setReview(selectedRowData.review || '');
      setStatus(selectedRowData.status || 'Waiting for Review');
    } else if (view && mode === 'create') {
      // Reset form for create mode
      setFirstName('');
      setLastInitial('');
      setReview('');
      setStatus('Waiting for Review');
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [view, selectedRowData, mode]);

  const close_menu = () => {
    setFirstName('');
    setLastInitial('');
    setReview('');
    setStatus('Waiting for Review');
    setFirstNameError(false);
    setLastInitialError(false);
    setReviewError(false);
    setSelectedRowData(null);
    setView(false);
    setIsLoading(false);
  };

  const submit = async () => {
    // Reset errors
    setFirstNameError(false);
    setLastInitialError(false);
    setReviewError(false);

    // Validate
    let hasError = false;
    if (firstName === '') {
      setFirstNameError(true);
      hasError = true;
    }
    if (lastInitial.length !== 1) {
      setLastInitialError(true);
      hasError = true;
    }
    if (review === '') {
      setReviewError(true);
      hasError = true;
    }

    if (hasError) {
      return;
    }

    try {
      if (!isMountedRef.current) return;
      setIsLoading(true);

      const data = {
        firstName: firstName,
        lastInitial: lastInitial,
        review: review,
        status: status || 'Waiting for Review',
      };

      if (mode === 'edit') {
        await updateDocument(selectedRowData.$id || selectedRowData.id, data, config.testimonialCollectionId);
      } else {
        // Create new testimonial
        await uploadData(data, config.testimonialCollectionId);
      }

      if (!isMountedRef.current) return;

      if (onSuccess) {
        onSuccess();
      }
      close_menu();
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Error saving testimonial:', error);
      alert('Failed to save testimonial. Please try again.');
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  if (!view) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={close_menu}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              {mode === 'create' ? 'Add New Testimonial' : 'Edit Testimonial'}
            </h2>
            <button
              onClick={close_menu}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <MdOutlineCancel className="text-2xl" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                title="First Name"
                placeholder="Enter first name"
                value={firstName}
                handleChangeText={setFirstName}
                error={firstNameError}
                required
              />

              <FormInput
                title="Last Initial"
                placeholder="Enter last initial (single letter)"
                value={lastInitial}
                handleChangeText={(value) => {
                  // Only allow single character
                  if (value.length <= 1) {
                    setLastInitial(value.toUpperCase());
                  }
                }}
                error={lastInitialError}
                required
              />
            </div>

            <DropdownField
              selected={status}
              setSelected={setStatus}
              title="Status"
              options={['Approved', 'Waiting for Review', 'Denied']}
              handleChangeText={setStatus}
              required
            />

            <MessageInput
              title="Review"
              value={review}
              placeholder="Enter the customer review..."
              handleChangeText={setReview}
              required
              error={reviewError}
            />

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={close_menu}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <Button
                bgColor={currentColor || '#1E4DB7'}
                text={mode === 'create' ? 'Create Testimonial' : 'Save Changes'}
                borderRadius="8px"
                color="white"
                isLoading={isLoading}
                onClick={submit}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EditTestimonial;
